import { FC, HTMLAttributes, ReactNode, useEffect, useState } from "react";
import cn from "classnames";

type NumberControlType = {
  plusAction?: ReactNode;
  minusAction?: ReactNode;
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  classNames?: {
    actions?: string[];
    display?: string;
  };
  className?: string;
  disabled?: boolean;
};

function clamp(value: number, min?: number, max?: number) {
  let result = value;

  if (min !== undefined) result = Math.max(result, min);
  if (max !== undefined) result = Math.min(result, max);

  return result;
}

const NumberControl: FC<NumberControlType & Omit<HTMLAttributes<HTMLDivElement>, "onChange">> = ({
  plusAction,
  minusAction,
  value,
  onChange,
  max,
  min,
  classNames,
  className,
  disabled,
  ...props
}) => {

  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<number>(
    clamp(value ?? 0, min, max)
  );

  useEffect(() => {
    if (isControlled) {
      const clamped = clamp(value as number, min, max);
      setInternalValue(clamped);
    }
  }, [value, min, max, isControlled]);

  const currentValue = isControlled
    ? clamp(value as number, min, max)
    : internalValue;

  const handleAddition = () => {
    const newValue = clamp(currentValue + 1, min, max);

    if (!isControlled) {
      setInternalValue(newValue);
    }

    onChange?.(newValue);
  };

  const handleSubtraction = () => {
    const newValue = clamp(currentValue - 1, min, max);

    if (!isControlled) {
      setInternalValue(newValue);
    }

    onChange?.(newValue);
  };

  return (
    <div
      className={cn(
        "min-w-25 flex items-center justify-between gap-4",
        className
      )}
      {...props}
    >
      <button
        onClick={handleSubtraction}
        className={cn(
          "w-6 h-6 flex items-center justify-center cursor-pointer select-none rounded-full border",
          classNames?.actions?.[0]
        )}
        disabled={disabled || currentValue <= (min ?? -Infinity)}
      >
        {minusAction}
      </button>

      <div className={cn("flex-2 text-center", classNames?.display)}>
        {currentValue}
      </div>

      <button
        onClick={handleAddition}
        className={cn(
          "w-6 h-6 flex items-center justify-center cursor-pointer select-none rounded-full border",
          classNames?.actions?.[1]
        )}
        disabled={disabled || currentValue >= (max ?? Infinity)}
      >
        {plusAction}
      </button>
    </div>
  );
};

export default NumberControl;
export type { NumberControlType };